import React, { useEffect, useRef } from 'react';
import { AccordionContent, AccordionHeader, AccordionItem, Icon, Typography } from 'gd-design-library';
import classnames from 'classnames/bind';
import styles from './OrderItemDesktop.module.scss';
import { OrderExpanded } from '@services/checkout';
import { Price, OrderLineItemInfo } from '@components';
import { useRouter } from 'next/router';

const CN = 'order-item-desktop';
const summaryClassName = `${CN}__summary`;
const summaryInnerClassName = `${summaryClassName}__inner`;
const blackBorderClassName = `${summaryInnerClassName}__black-border`;
const orderNumberClassName = `${summaryInnerClassName}__order-number`;
const orderInfoClassName = `${summaryInnerClassName}__order-info`;
const totalPriceClassName = `${summaryInnerClassName}__total-price`;
const totalPriceValueClassName = `${totalPriceClassName}__value`;
const createdAtClassName = `${summaryInnerClassName}__created-at`;
const imageClassName= `${summaryInnerClassName}__image`;
const titleClassName = `${summaryInnerClassName}__title`;
const detailsClassName = `${CN}__details`;
const generalInfoClassName = `${detailsClassName}__general-info`;
const infoSectionClassName = `${generalInfoClassName}__info-section`;
const cartInfoClassName = `${detailsClassName}__cart-info`;
const footerRowClassName = `${cartInfoClassName}__footer-row`;

const cx = classnames.bind(styles);

interface Props {
  className?: string;
  order: OrderExpanded;
  imageUrl?: string;
  lineItemQuantity: number;
  isExpanded?: boolean;
}

export const OrderItemDesktop: React.FC<Props> = ({
  className,
  order: {
    id,
    orderNumber,
    lineItems,
    createdAt,
    orderState,
    totalPrice,
    shippingAddress,
    shippingPrice
  },
  imageUrl,
  lineItemQuantity,
  isExpanded
}) => {
  const { query } = useRouter();
  const accordionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (query['order_id'] === id && accordionRef.current) {
      accordionRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [ query, id ]);

  return (
    <AccordionItem className={ `${cx(CN)} ${className}` } id={ id }>
      <AccordionHeader
        className={ cx(summaryClassName) }
        expandIcon={ <Icon name='arrowDown' width={ 24 } height={ 24 } /> }
      >
        <div className={ cx(summaryInnerClassName) }>
          <div className={ cx(blackBorderClassName) } />
          <div className={ cx(orderInfoClassName) }>
            <Typography className={ cx(orderNumberClassName) }>{orderNumber}</Typography>
            <Typography variant='caption' as='span'>{ `${orderState} (${lineItemQuantity} items)` }</Typography>
          </div>
          <div className={ cx(createdAtClassName) }>
            <Typography className={ cx(titleClassName) }>Created At</Typography>
            <Typography variant='caption' as='span'>
              {new Date(createdAt).toLocaleDateString()} {new Date(createdAt).toLocaleTimeString()}
            </Typography>
          </div>
          {!isExpanded && <div className={ cx(totalPriceClassName) }>
            <Typography className={ cx(titleClassName) }>Total</Typography>
            <Price className={ cx(totalPriceValueClassName) } price={ totalPrice } />
          </div>}
          {!isExpanded && imageUrl && <img className={ cx(imageClassName) } src={ imageUrl } />}
        </div>
      </AccordionHeader>
      <AccordionContent className={ cx(detailsClassName) }>
        <div className={ cx(generalInfoClassName) } ref={ accordionRef }>
          <div className={ cx(infoSectionClassName) }>
            <Typography variant='small' as='span' className={ cx(titleClassName) }>Address</Typography>
          </div>
          <div className={ cx(infoSectionClassName) }>
            <Typography variant='small' as='span'> { `${shippingAddress.streetName ?? ''} ${shippingAddress.building ?? ''}` } </Typography>
            <Typography variant='small' as='span'> { `${shippingAddress.state ?? ''} ${shippingAddress.city ?? ''} ${shippingAddress.postalCode ?? ''}` } </Typography>
          </div>
          <div className={ cx(infoSectionClassName) }>
            <Typography variant='small' as='span'> { `${shippingAddress.firstName ?? ''} ${shippingAddress.lastName ?? ''}` } </Typography>
            <Typography variant='small' as='span'> { shippingAddress.email ?? '' } </Typography>
            <Typography variant='small' as='span'> { shippingAddress.phone ?? '' } </Typography>
          </div>
        </div>
        <div className={ cx(cartInfoClassName) }>
          <Typography className={ cx(titleClassName) } variant='caption' as='span'>Item</Typography>
          {lineItems.map(lineItem => (
            <OrderLineItemInfo key={ lineItem.id } lineItem={ lineItem } />
          ))}
          {shippingPrice && <div className={ cx(footerRowClassName) }>
            <Typography className={ cx(titleClassName) } variant='caption' as='span'>Shipping</Typography>
            <Typography variant='caption' as='span'><Price price={ shippingPrice } /></Typography>
          </div>}
          <div className={ cx(footerRowClassName) }>
            <Typography className={ cx(titleClassName) } variant='caption' as='span'>Total</Typography>
            <Typography variant='h4'><Price price={ totalPrice } /></Typography>
          </div>
        </div>
      </AccordionContent>
    </AccordionItem>
  );
};
